import React, { FunctionComponent } from "react";
import {
  Plan,
  PlanCategoryType,
  SupplementalBenefitCategoryTypeNames,
  PlanSNPType,
  AdditionalSupplementalBenefitCoverage,
} from "../../../@types";
import { FormattedMessage } from "react-intl-phraseapp";
import { getAdditionalBenefit } from "../../../helpers";

export const zeroDollarCopay = "$0";
export const zeroPercentCoinsurance = "0%";

export const OtherServicesBenefitDetails: FunctionComponent<{
  t: (id: string) => string;
  plan: Plan;
  category: SupplementalBenefitCategoryTypeNames;
  suppressed: boolean;
}> = ({ t, plan, category, suppressed }) => {
  let inNetworkCopay = "";
  let inNetworkCoinsurance: string | null = "";
  let outOfNetworkCopay = "";
  let outOfNetworkCoinsurance = "";
  let useInNetworkLabel = true;
  let useCustomString = false;

  const servicesCategory = plan.additional_supplemental_benefits?.other_benefits.find(
    c => c.category === category
  );

  if (servicesCategory?.benefits.length) {
    const benefit = servicesCategory.benefits[0];

    if (benefit.info) {
      const {
        coins_max_pct,
        coins_min_pct,
        copay_max,
        copay_min,
        oon_coins_max_pct,
        oon_coins_min_pct,
        oon_copay_max,
        oon_copay_min,
      } = benefit.info;

      const { non_zero_dollar_dsnp } = plan;

      if (copay_min !== null) {
        inNetworkCopay = `$${copay_min}`;

        if (copay_max !== null && copay_max !== copay_min) {
          inNetworkCopay = `$${copay_min}-$${copay_max}`;
        }

        // If non $0 DSNP, $0 or $5
        if (non_zero_dollar_dsnp && copay_min > 0) {
          inNetworkCopay = `$0 ${t("or")} $${copay_min}`;
        }
      }

      if (coins_min_pct !== null) {
        inNetworkCoinsurance = `${coins_min_pct}%`;

        if (coins_max_pct !== null && coins_max_pct !== coins_min_pct) {
          inNetworkCoinsurance = `${coins_min_pct}%-${coins_max_pct}%`;
        }

        // If non $0 DSNP, 0% or 5%
        if (non_zero_dollar_dsnp && coins_min_pct > 0) {
          inNetworkCoinsurance = `0% ${t("or")} ${coins_min_pct}%`;
        }

        // Don't show inNetworkCoinsurance if inNetworkCopay is just $0
        if (
          inNetworkCopay === zeroDollarCopay &&
          inNetworkCoinsurance === zeroPercentCoinsurance
        ) {
          inNetworkCoinsurance = null;
        }
      }

      if (oon_copay_min !== null) {
        outOfNetworkCopay = `$${oon_copay_min}`;

        if (oon_copay_max !== null && oon_copay_max !== oon_copay_min) {
          outOfNetworkCopay = `$${oon_copay_min}-$${oon_copay_max}`;
        }
      }

      if (oon_coins_min_pct !== null) {
        outOfNetworkCoinsurance = `${oon_coins_min_pct}%`;

        if (
          oon_coins_max_pct !== null &&
          oon_coins_max_pct !== oon_coins_min_pct
        ) {
          outOfNetworkCoinsurance = `${oon_coins_min_pct}%-${oon_coins_max_pct}%`;
        }
      }
    }
  }

  const isMsaOrMmp = [
    PlanCategoryType.MEDICARE_MEDICAID_PLAN,
    PlanCategoryType.MSA,
  ].includes(plan.category);

  const isHmoAndDsnp =
    plan.category === PlanCategoryType.HMO &&
    plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE;

  if (isMsaOrMmp || isHmoAndDsnp) {
    useInNetworkLabel = false;
  }

  if (isMsaOrMmp && inNetworkCoinsurance === "") {
    inNetworkCoinsurance = getPlanCategoryBenefitLabel(category, plan.category);

    useCustomString = true;
  }

  if (suppressed) {
    return (
      <ul id="other-services-benefit-item-list" className="ds-c-list--bare">
        <li>
          <div className="ds-u-display--flex ds-u-align-items--center">
            <p className="ds-u-margin--0">{t("suppressed_plan_benefit")}</p>
          </div>
        </li>
      </ul>
    );
  }
  return (
    <ul id="other-services-benefit-item-list" className="ds-c-list--bare">
      {(inNetworkCopay || inNetworkCoinsurance) && (
        <li>
          <div className="ds-u-display--flex ds-u-align-items--center">
            {useInNetworkLabel && (
              <p className="ds-u-margin--0" data-testid="benefit-in-network">
                {t("plan_details.benefit.in_network")}
              </p>
            )}
            <ul
              className={`ds-c-list--bare ${
                useInNetworkLabel
                  ? "ds-u-margin-left--1"
                  : "ds-u-margin-left--0"
              }`}
            >
              {!useCustomString && inNetworkCopay && (
                <li id="osbl-in-network-copay">
                  <FormattedMessage
                    id="plan_details.other_services.copay"
                    values={{ copayRange: inNetworkCopay }}
                  />
                </li>
              )}
              {inNetworkCoinsurance && (
                <li id="osbl-in-network-coinsurance">
                  {useCustomString ? (
                    t(inNetworkCoinsurance)
                  ) : (
                    <FormattedMessage
                      id="plan_details.other_services.coinsurance"
                      values={{ coinsuranceRange: inNetworkCoinsurance }}
                    />
                  )}
                </li>
              )}
            </ul>
          </div>
        </li>
      )}

      {(outOfNetworkCopay || outOfNetworkCoinsurance) && (
        <li>
          <div className="ds-u-display--flex ds-u-align-items--center">
            <p className="ds-u-margin--0">
              {t("plan_details.benefit.oo_network")}
            </p>
            <ul className="ds-c-list--bare  ds-u-margin-left--1">
              {outOfNetworkCopay && (
                <li id="osbl-oo-network-copay">
                  <FormattedMessage
                    id="plan_details.other_services.copay"
                    values={{ copayRange: outOfNetworkCopay }}
                  />
                </li>
              )}
              {outOfNetworkCoinsurance && (
                <li id="osbl-oo-network-coinsurance">
                  <FormattedMessage
                    id="plan_details.other_services.coinsurance"
                    values={{
                      coinsuranceRange: outOfNetworkCoinsurance,
                    }}
                  />
                </li>
              )}
            </ul>
          </div>
        </li>
      )}
    </ul>
  );
};

export const getReferralAndAuthorizationRequired = (
  plan: Plan,
  category: SupplementalBenefitCategoryTypeNames
): {
  authorizationRequired: boolean;
  referralRequired: boolean;
} => {
  let authorizationRequired = false;
  let referralRequired = false;
  const servicesCategory = plan.additional_supplemental_benefits?.other_benefits.find(
    c => c.category === category
  );
  if (servicesCategory?.benefits.length) {
    const benefit = servicesCategory.benefits[0];

    if (benefit.info) {
      const { authorization_required, referral_required } = benefit.info;
      authorizationRequired = authorization_required;
      referralRequired = referral_required;
    }
  }
  return { authorizationRequired, referralRequired };
};

export const PlanBenefitCategoryLabels = {
  [PlanCategoryType.MSA]: "plan_details.other_benefits.msa_copay_message",
  [PlanCategoryType.MEDICARE_MEDICAID_PLAN]:
    "plan_details.other_benefits.mmp_copay_message",
};

const getPlanCategoryBenefitLabel = (
  category: string,
  planType: PlanCategoryType
): string => {
  const labels = {
    [SupplementalBenefitCategoryTypeNames.SB_CAT_OPIOID_TREATMENT_SERVICES]: {
      ...PlanBenefitCategoryLabels,
    },
    [SupplementalBenefitCategoryTypeNames.SB_CAT_DIALYSIS_SERVICES]: {
      ...PlanBenefitCategoryLabels,
    },
  };

  return (labels[category] && labels[category][planType]) || "";
};

export const getMMPBenefitsCoverageKey = (
  plan: Plan,
  category: string,
  benefit: string
): string => {
  const detail = getAdditionalBenefit(plan, "mmp_benefits", category, benefit);
  let coverageKey = "";
  if (detail) {
    if (
      detail.coverage ===
      AdditionalSupplementalBenefitCoverage.SB_COVERAGE_SOME_COVERAGE
    ) {
      coverageKey = detail.coverage + "_MMP";
    } else {
      coverageKey = detail.coverage;
    }
  }

  return coverageKey;
};
